import { z } from "zod";
import { appRoleSchema } from "./auth.js";
import {
  booleanQuerySchema,
  isoDateTimeSchema,
  searchQuerySchema,
  userRefSchema,
} from "./common.js";

/**
 * The admin surface — allow list, users, audit log. See docs/06-api.md#admin.
 *
 * The audit action vocabulary lives here rather than on the server because the
 * viewer's filter has to offer every action the app can write. A server-only
 * union would leave the dropdown to be retyped by hand, and a dropdown that is
 * missing an action is a filter that silently hides rows.
 */

export const auditActionSchema = z.enum([
  "auth.login",
  "auth.denied",
  "auth.logout",
  "session.revoked",
  "allowlist.create",
  "allowlist.update",
  "allowlist.revoke",
  "product.create",
  "product.update",
  "product.archive",
  "product.member.add",
  "product.member.remove",
  "product.transfer",
  "scenario.create",
  "scenario.update",
  "scenario.save",
  "scenario.duplicate",
  "scenario.archive",
  "scenario.delete",
  "scenario.restore",
  "scenario.share",
  "scenario.unshare",
  "scenario.export",
  "scenario.import",
  "scenario.compare",
]);
export type AuditActionName = z.infer<typeof auditActionSchema>;

export const AUDIT_ACTIONS: readonly AuditActionName[] = auditActionSchema.options;

/** The filter's option groups, in the order an administrator thinks about them. */
export const AUDIT_ACTION_GROUPS: { label: string; prefix: string }[] = [
  { label: "Sign-in", prefix: "auth." },
  { label: "Sessions", prefix: "session." },
  { label: "Allow list", prefix: "allowlist." },
  { label: "Products", prefix: "product." },
  { label: "Scenarios", prefix: "scenario." },
];

/* ---------------------------------------------------------------- allow list */

export const allowListRowSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  role: appRoleSchema,
  note: z.string().nullable(),
  /** The administrator who added them; null once that account is deleted. */
  invitedBy: userRefSchema.nullable(),
  /** Null until they have signed in at least once — the "invited, not arrived" case. */
  lastLoginAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  /** Set = access denied, history kept. */
  revokedAt: isoDateTimeSchema.nullable(),
});
export type AllowListRow = z.infer<typeof allowListRowSchema>;

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email("Enter a valid email address.")
  .max(254);

export const createAllowListEntrySchema = z.object({
  email: emailSchema,
  role: appRoleSchema.default("VIEWER"),
  /** Why this person has access — the thing nobody can reconstruct a year later. */
  note: z.string().trim().max(500).nullish(),
});
export type CreateAllowListEntryRequest = z.infer<typeof createAllowListEntrySchema>;

export const updateAllowListEntrySchema = z
  .object({
    role: appRoleSchema,
    note: z.string().trim().max(500).nullable(),
  })
  .partial();
export type UpdateAllowListEntryRequest = z.infer<typeof updateAllowListEntrySchema>;

export const allowListQuerySchema = z.object({
  q: searchQuerySchema,
  /** Off by default: the list answers "who has access", not "who ever did". */
  includeRevoked: booleanQuerySchema,
});
export type AllowListQuery = z.infer<typeof allowListQuerySchema>;

/* --------------------------------------------------------------------- users */

export const adminUserRowSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  fullName: z.string(),
  nickName: z.string().nullable(),
  role: appRoleSchema,
  lastLoginAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  /** Scenarios they wrote, archived ones included — this is a record, not a list. */
  scenarioCount: z.number().int(),
  productCount: z.number().int(),
  /** False for someone whose access has been revoked but whose work is still here. */
  allowListed: z.boolean(),
});
export type AdminUserRow = z.infer<typeof adminUserRowSchema>;

export const adminUserQuerySchema = z.object({
  q: searchQuerySchema,
});
export type AdminUserQuery = z.infer<typeof adminUserQuerySchema>;

/* ----------------------------------------------------------------- audit log */

export const auditRowSchema = z.object({
  id: z.string(),
  actorId: z.string().nullable(),
  /** Kept even when there is no user row — the whole point for `auth.denied`. */
  actorEmail: z.string(),
  actor: userRefSchema.nullable(),
  /**
   * A plain string, not `auditActionSchema`. A row written by an older build can
   * name an action this build no longer has, and refusing to render the page
   * because of it would be exactly backwards for an audit log.
   */
  action: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  summary: z.string().nullable(),
  metadata: z.unknown(),
  ip: z.string().nullable(),
  createdAt: isoDateTimeSchema,
});
export type AuditRow = z.infer<typeof auditRowSchema>;

export const auditQuerySchema = z.object({
  actorId: z.string().trim().min(1).max(64).optional(),
  action: z.string().trim().min(1).max(64).optional(),
  /** `YYYY-MM-DD` from a date input, or a full ISO timestamp. */
  from: z.string().trim().min(1).max(40).optional(),
  to: z.string().trim().min(1).max(40).optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The range a pair of date inputs means.
 *
 * `to=2026-09-15` has to include everything that happened on the 15th, so the
 * upper bound is the *next* midnight and the comparison is `<`. Doing it the
 * obvious way — `createdAt <= 2026-09-15T00:00Z` — silently drops a whole day,
 * which in an audit log is the kind of bug you only notice when it matters.
 */
export function auditRangeBounds(from?: string, to?: string): { gte?: Date; lt?: Date } {
  const bounds: { gte?: Date; lt?: Date } = {};

  if (from) {
    const start = new Date(DATE_ONLY.test(from) ? `${from}T00:00:00.000Z` : from);
    if (!Number.isNaN(start.getTime())) bounds.gte = start;
  }
  if (to) {
    const end = new Date(DATE_ONLY.test(to) ? `${to}T00:00:00.000Z` : to);
    if (!Number.isNaN(end.getTime())) {
      bounds.lt = DATE_ONLY.test(to) ? new Date(end.getTime() + DAY_MS) : end;
    }
  }
  return bounds;
}

/**
 * Pasted emails, one per line or comma-separated, de-duplicated in the order
 * they were typed. Onboarding a team means pasting the list someone sent you,
 * and that list is never one address per request.
 */
export function parseEmailList(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of text.split(/[\s,;]+/)) {
    const candidate = part
      .trim()
      .toLowerCase()
      .replace(/^[<"']+|[>"',.]+$/g, "");
    if (!candidate) continue;
    if (seen.has(candidate)) continue;
    seen.add(candidate);
    out.push(candidate);
  }
  return out;
}
